import { Adapter } from './adapter';
import { CarInventoryMovementsModel } from '../../../carManagement';
import { BookingPosition } from '../../types';
import { Log } from '../../util/log';

/**
 * Liest/schreibt das lokale Fahrzeuglager (`CAR_STORAGE`).
 */
export class CarAdapter extends Adapter {
  private static readonly STORAGE_KEY = 'CAR_STORAGE';

  /**
   * Aktuelle Menge eines Artikels im Fahrzeuglager. 0, wenn der Artikel fehlt
   * oder gar kein lokales Inventar vorhanden ist.
   *
   * @param articleId ID des Artikels
   * @returns Menge im Fahrzeuglager, 0 als Fallback
   */
  public getArticleAmountLocal(articleId: number): number {
    const position = this.readLocal()?.inventory_articles?.find(
      p => p.article_id === articleId,
    );
    return position?.amount ?? 0;
  }

  /**
   * Ist ein lokales Fahrzeuglager vorhanden? Ohne Inventar gibt es keine
   * Buchungsbasis → die Buchung muss abgelehnt werden.
   *
   * @returns `true`, wenn ein Fahrzeuglager existiert.
   */
  public hasLocalInventory(): boolean {
    return this.readLocal() !== null;
  }

  /**
   * Verändert den lokalen Fahrzeugbestand eines Artikels um `delta`
   * (+delta = Auto bekommt, -delta = Auto gibt ab).
   *
   * @param position Buchungsposition (liefert die Artikel-ID)
   * @param delta Bestandsänderung (negativ = Abgang)
   * @returns false, wenn kein lokales Inventar existiert oder das Speichern
   *          fehlschlägt. Dann wurde nichts geschrieben.
   */
  public async updatePositionAmountLocal(
    position: BookingPosition,
    delta: number,
  ): Promise<boolean> {
    const inventory = this.readLocal();
    if (!inventory) {
      return false;
    }
    if (!inventory.inventory_articles) {
      inventory.inventory_articles = [];
    }
    const existing = inventory.inventory_articles.find(
      p => p.article_id === position.article_id,
    );
    if (existing) {
      existing.amount += delta;
    } else {
      inventory.inventory_articles.push({
        article_id: position.article_id,
        amount: delta,
      });
    }
    inventory.updated_at = new Date().toISOString();
    return this.saveLocal(inventory);
  }

  /**
   * Verändert den lokalen Disposal-Eintrag (`disposal_articles`) eines Artikels
   * um `delta` - das eigene Entsorgungs-Lager des Fahrzeugs. Movement-los; die
   * echte Backend-Buchung läuft beim Auftragsabschluss (`WithObject
   * storage:'disposal'`).
   *
   * @param position Buchungsposition (liefert die Artikel-ID)
   * @param delta Bestandsänderung des Disposal-Lagers (negativ = Abgang)
   * @returns false, wenn kein lokales Inventar existiert oder das Speichern
   *          fehlschlägt. Dann wurde nichts geschrieben.
   */
  public async updateDisposalAmountLocal(
    position: BookingPosition,
    delta: number,
  ): Promise<boolean> {
    const inventory = this.readLocal();
    if (!inventory) {
      return false;
    }
    if (!inventory.disposal_articles) {
      inventory.disposal_articles = [];
    }
    const existing = inventory.disposal_articles.find(
      p => p.article_id === position.article_id,
    );
    if (existing) {
      existing.amount += delta;
    } else {
      inventory.disposal_articles.push({
        article_id: position.article_id,
        amount: delta,
      });
    }
    inventory.updated_at = new Date().toISOString();
    return this.saveLocal(inventory);
  }

  private readLocal(): CarInventoryMovementsModel | null {
    try {
      const raw = localStorage.getItem(CarAdapter.STORAGE_KEY);
      if (!raw) {
        return null;
      }
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object'
        ? (parsed as CarInventoryMovementsModel)
        : null;
    } catch (error) {
      Log.error('CarAdapter.readLocal', error);
      return null;
    }
  }

  private saveLocal(inventory: CarInventoryMovementsModel): boolean {
    try {
      localStorage.setItem(CarAdapter.STORAGE_KEY, JSON.stringify(inventory));
      return true;
    } catch (error) {
      Log.error('CarAdapter.saveLocal', error, {
        name: inventory.name,
        id: inventory.id,
        remote_id: inventory.remote_id,
      });
      return false;
    }
  }
}
